"use client"

import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { PromptDiff } from "@/components/ui/prompt-diff"
import { useTranslations } from "next-intl"


interface PromptConflictBannerProps {
  /** The instructions as someone else saved them. */
  latest: string
  /** What is in the editor now. */
  mine: string
  saving: boolean
  onReload: () => void
  onSaveMine: () => void
}

/** A save refused because the instructions changed under the editor; the typed text is kept. */
export function PromptConflictBanner({ latest, mine, saving, onReload, onSaveMine }: PromptConflictBannerProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsPromptConflictBanner")

  const [showDiff, setShowDiff] = useState(false)

  return (
    <Alert className="border-amber-200 bg-amber-50">
      <AlertDescription className="space-y-3 text-amber-900">
        <p><span key={tUi("theseInstructionsChangedWhileYouWereEditingYour")}>{tUi("theseInstructionsChangedWhileYouWereEditingYour")}</span></p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowDiff((shown) => !shown)}>
            <span key={showDiff ? tUi("hideWhatChanged") : tUi("showWhatChanged")}>{showDiff ? tUi("hideWhatChanged") : tUi("showWhatChanged")}</span>
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onReload} disabled={saving}>
            <span><span key={tUi("reloadLatest")}>{tUi("reloadLatest")}</span></span>
          </Button>
          <Button type="button" size="sm" onClick={onSaveMine} disabled={saving}>
            <span><span key={tUi("saveMineAnyway")}>{tUi("saveMineAnyway")}</span></span>
          </Button>
        </div>
        {showDiff && (
          <div className="max-h-80 overflow-auto rounded-md border bg-background p-2 text-foreground">
            <PromptDiff prev={latest} next={mine} labels={{ prev: "Latest saved", next: "Your text" }} />
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}
